/**
 * Blog / Journal hub. Reads published posts from GET /api/blog (public).
 * Posts are AI-assisted and admin-reviewed (see regis-admin). Luxury design.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';

interface Post {
  slug: string;
  title: string;
  excerpt: string | null;
  category: string | null;
  read_minutes: number | null;
  published_at: string | null;
}

function CategoryTag({ label }: { label: string }) {
  return (
    <span className="text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.18em', color: 'var(--champagne)' }}>
      {label}
    </span>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : iso + 'Z');
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

const tile = (i: number) =>
  `radial-gradient(120% 120% at ${20 + i * 10}% 0%, rgba(201,168,106,0.20) 0%, rgba(201,168,106,0) 60%), linear-gradient(135deg, var(--ink-3), var(--ink-2))`;

export default function Blog() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ posts: Post[] }>('/api/blog')
      .then((d) => setPosts(d.posts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <>
      <PageHeader title="The Journal" subtitle="Training, nutrition & news" />

      <div className="px-4 flex flex-col gap-4">
        {loading ? (
          [1, 2, 3].map((i) => <div key={i} className="card animate-pulse h-28" style={{ background: 'var(--surface)' }} />)
        ) : posts.length === 0 ? (
          <div className="card text-center py-10">
            <p className="font-semibold" style={{ color: 'var(--cream)' }}>No stories yet</p>
            <p className="text-sm mt-1" style={{ color: 'var(--cream-muted)' }}>Check back soon for training & nutrition notes.</p>
          </div>
        ) : (
          <>
            {featured && (
              <Link to={`/community/${featured.slug}`}>
                <article className="card cursor-pointer">
                  <div aria-hidden style={{ height: 150, borderRadius: 'var(--radius-md)', marginBottom: 16, background: tile(0), border: '1px solid var(--ink-line, rgba(244,239,227,0.08))' }} />
                  {featured.category && <CategoryTag label={featured.category} />}
                  <h2 className="display mt-2 mb-2" style={{ fontSize: '1.6rem', lineHeight: 1.15 }}>{featured.title}</h2>
                  {featured.excerpt && <p className="text-sm" style={{ color: 'var(--cream-muted)' }}>{featured.excerpt}</p>}
                  <p className="text-xs mt-3" style={{ color: 'var(--cream-muted)' }}>
                    {fmtDate(featured.published_at)}{featured.read_minutes ? ` · ${featured.read_minutes} min read` : ''}
                  </p>
                </article>
              </Link>
            )}

            {rest.map((post, i) => (
              <Link key={post.slug} to={`/community/${post.slug}`}>
                <article className="card cursor-pointer flex gap-4 items-start">
                  <div aria-hidden className="flex-shrink-0" style={{ width: 72, height: 72, borderRadius: 'var(--radius-md)', background: tile(i + 1), border: '1px solid var(--ink-line, rgba(244,239,227,0.08))' }} />
                  <div className="min-w-0">
                    {post.category && <CategoryTag label={post.category} />}
                    <h3 className="display mt-1 mb-1" style={{ fontSize: '1.15rem', lineHeight: 1.2 }}>{post.title}</h3>
                    <p className="text-xs" style={{ color: 'var(--cream-muted)' }}>
                      {fmtDate(post.published_at)}{post.read_minutes ? ` · ${post.read_minutes} min read` : ''}
                    </p>
                  </div>
                </article>
              </Link>
            ))}
          </>
        )}
      </div>
    </>
  );
}
